import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { sentrySafeHttpPath } from '../utils/sentry-telemetry.util';

/**
 * Structured technical logs to Sentry (English messages + locale tag).
 * User-facing copy stays in Translate/Toastr. / سجلات تقنية للـ Sentry مع وسم اللغة
 */
@Injectable({
  providedIn: 'root',
})
export class MonitoringLogService {
  private readonly localization = inject(LocalizationService);

  private localeTag(): string {
    return this.localization.getCachedLanguage() ?? 'en';
  }

  /** Server errors and transport failures (path + status only). / أخطاء الخادم أو الشبكة */
  logHttpTransportOrServerFailure(status: number, requestUrl: string): void {
    const path = sentrySafeHttpPath(requestUrl);
    const locale = this.localeTag();
    Sentry.captureMessage(
      `HTTP ${status === 0 ? 'network_or_cors' : status} on ${path}`,
      {
        level: status === 0 ? 'warning' : 'error',
        tags: {
          locale,
          source: 'angular-http',
          kind: 'transport_or_5xx',
        },
        fingerprint: ['http-severe', String(status), path],
      }
    );
  }

  /** Optional technical warnings (sample at call sites if noisy). / تحذيرات تقنية */
  technicalWarning(message: string, data?: Record<string, string | number | boolean>): void {
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: { locale: this.localeTag(), source: 'angular-app' },
      extra: data ?? {},
    });
  }

  /** Optional technical info as breadcrumb (does not create an Issue). / معلومات كـ breadcrumb */
  technicalInfoBreadcrumb(category: string, message: string, data?: Record<string, string | number | boolean>): void {
    Sentry.addBreadcrumb({
      category,
      message,
      level: 'info',
      data: { ...data, locale: this.localeTag() },
    });
  }
}
